import { gsap } from "gsap";
import Alpine from "alpinejs";

/** Resolves once every background image inside `els` has loaded (failed images resolve too). */
const preloadImages = (els: NodeListOf<HTMLElement>) =>
  Promise.all(
    Array.from(els).map((el) => {
      const url = getComputedStyle(el).backgroundImage.match(/url\(["']?(.*?)["']?\)/)?.[1];
      if (!url) return Promise.resolve();

      const img = new Image();
      img.src = url;
      return img.decode().catch(() => undefined);
    })
  );

const lerp = (a: number, b: number, n: number) => (1 - n) * a + n * b;

/**
 * Shows the next image at the mouse position every time the mouse travelled `threshold` px,
 * gliding in from the previous position and then shrinking away (Codrops "Image Trail", demo 1).
 * Images are position: fixed, so positions are plain viewport (client) coordinates and the
 * images are never clipped by an overflow: hidden ancestor.
 */
class ImageTrail {
  private el: HTMLElement;
  private images: HTMLElement[];
  private threshold = 80;
  private imgPosition = 0;
  private zIndex = 1;
  private mouse = { x: 0, y: 0 };
  private last = { x: 0, y: 0 };
  private cache = { x: 0, y: 0 };
  private rafId = 0;
  private isActive = false;

  constructor(el: HTMLElement) {
    this.el = el;
    this.images = Array.from(el.querySelectorAll<HTMLElement>(".c-image-trail__img"));

    // the trail sits above the content, so listen on its parent section
    this.el.parentElement?.addEventListener("pointermove", this.onPointerMove);
  }

  private onPointerMove = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") return;

    this.mouse = { x: e.clientX, y: e.clientY };

    // first move: start everything at the cursor so the first image doesn't fly in from 0/0
    if (!this.isActive) {
      this.isActive = true;
      this.cache = { ...this.mouse };
      this.last = { ...this.mouse };
      this.rafId = requestAnimationFrame(this.render);
    }
  };

  private render = () => {
    const distance = Math.hypot(this.mouse.x - this.last.x, this.mouse.y - this.last.y);

    this.cache.x = lerp(this.cache.x, this.mouse.x, 0.1);
    this.cache.y = lerp(this.cache.y, this.mouse.y, 0.1);

    if (distance > this.threshold) {
      this.showNextImage();
      this.last = { ...this.mouse };
    }

    this.rafId = requestAnimationFrame(this.render);
  };

  private showNextImage() {
    if (!this.images.length) return;

    this.zIndex++;
    this.imgPosition = (this.imgPosition + 1) % this.images.length;
    const img = this.images[this.imgPosition];
    const halfW = img.offsetWidth / 2;
    const halfH = img.offsetHeight / 2;

    gsap.killTweensOf(img);
    gsap
      .timeline()
      .fromTo(
        img,
        { opacity: 1, scale: 1, zIndex: this.zIndex, x: this.cache.x - halfW, y: this.cache.y - halfH },
        { duration: 0.4, ease: "power1", x: this.mouse.x - halfW, y: this.mouse.y - halfH },
        0
      )
      .fromTo(img.firstElementChild, { scale: 2 }, { duration: 0.4, ease: "power1", scale: 1 }, 0)
      .to(img, { duration: 0.5, ease: "power3", opacity: 0, scale: 0.2 }, 0.6);
  }

  destroy() {
    cancelAnimationFrame(this.rafId);
    this.el.parentElement?.removeEventListener("pointermove", this.onPointerMove);
    gsap.killTweensOf(this.images);
  }
}

Alpine.data("imageTrail", () => ({
  trail: null as ImageTrail | null,
  init() {
    preloadImages(this.$el.querySelectorAll<HTMLElement>(".c-image-trail__img-inner")).then(() => {
      this.trail = new ImageTrail(this.$el);
    });
  },
  destroy() {
    this.trail?.destroy();
  },
}));
